import { CanvasNodeIntent, CanvasEdgeIntent } from '../types/canvasIntent';

/**
 * Applies a semantic intent to a Cytoscape element.
 *
 * Algorithms should use this instead of direct addClass() or style() calls.
 * The intent is stored in data('canvas_intent') and also as a CSS class
 * for easy stylesheet targeting.
 *
 * Diff semantics (added 2026-05-04 — see CANVAS_INTENT_MIGRATION_PLAN.md
 * Bug 1): re-applying a different intent to the same element clears the
 * previous intent class so we don't accumulate (`target-root critical-endpoint`
 * stuck on one node was a real bug — observed in investigation
 * 73197ddc-7106-45a4-bd6f-c575eb08df82). Promoting a node to TARGET_ROOT
 * also demotes any other node currently flagged as root, since "root" is
 * a uniqueness invariant the algorithm intent system never enforced.
 */
export function applyCanvasIntent(
  element: any,
  intent: CanvasNodeIntent | CanvasEdgeIntent
): void {
  // Drop the previous intent class so it doesn't accumulate alongside the new one
  const prev = element.data && element.data('canvas_intent') as string | undefined;
  if (prev && prev !== intent) {
    element.removeClass(prev);
  }

  // Store in data for stylesheet [canvas_intent = "..."] selectors
  element.data('canvas_intent', intent);

  // Also add as class for direct .target-root selectors and backward compat
  element.addClass(intent);

  // Handle specific logic for certain intents if needed
  if (intent === CanvasNodeIntent.TARGET_ROOT) {
    // Demote any OTHER node currently flagged as root. There can be only
    // one. Without this, every algorithm that promotes its own root (auto-
    // trace target, FTM target, backward-trace source) leaves the previous
    // root marked, so the canvas ends up with multiple `is_root=true` nodes
    // and the inspector star-prefix appears on several at once.
    try {
      const cy = typeof element.cy === 'function' ? element.cy() : null;
      if (cy) {
        cy.nodes('[?is_root]').forEach((other: any) => {
          if (other.id() === element.id()) return;
          other.data('is_root', false);
          // Strip the stale target-root class so the previous root stops
          // matching `.target-root` selectors.
          other.removeClass(CanvasNodeIntent.TARGET_ROOT);
          if (other.data('canvas_intent') === CanvasNodeIntent.TARGET_ROOT) {
            other.data('canvas_intent', CanvasNodeIntent.STANDARD);
          }
        });
      }
    } catch {
      // Element isn't on a live cy core (e.g. a plain JSON element pre-add).
      // Skip the demotion — the next live application will catch it.
    }
    element.data('is_root', true);
  }

  if (intent === CanvasEdgeIntent.PRIMARY_TRACE) {
    element.data('edgeType', 'gold-path'); // Backward compat
  }
}
